import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useChat } from '@ai-sdk/react';
import { baseURL, PAYMENT_REQUIRED_EVENT } from '@/lib/axios';
import { DefaultChatTransport, generateId, lastAssistantMessageIsCompleteWithToolCalls, type PrepareSendMessagesRequest, type UIMessage } from 'ai';
import { ChatContext, ChatContextType } from './ChatContextTypes';
import Cookies from 'js-cookie';
import type { PersistedFeedbackStruct } from '@/api/types/workstation.types';

type MessageWithFeedback = UIMessage & {
  feedback?: PersistedFeedbackStruct;
  lastSearchFolders?: string[];
  metadata?: {
    lastSearchFolders?: string[];
    [key: string]: unknown;
  };
};

export function ChatProvider({ children }: { children: ReactNode }) {
  const [chatId, setChatId] = useState(() => generateId());
  const [deepSearch, setDeepSearch] = useState(false);
  const [externalSearch, setExternalSearch] = useState(false);
  const [initialMessages, setInitialMessages] = useState<UIMessage[]>([]);
  const [feedbacksMap, setFeedbacksMap] = useState<Record<string, PersistedFeedbackStruct>>({});
  const [foldersId, setFoldersId] = useState<string[]>([]);
  const [input, setInput] = useState('');

  const handlePaymentRequired = useCallback(() => {
    const paymentRequiredEvent = new CustomEvent(PAYMENT_REQUIRED_EVENT);
    window.dispatchEvent(paymentRequiredEvent);
  }, []);

  const transport = useMemo(() => {
    const prepareRequest: PrepareSendMessagesRequest<UIMessage> = ({ messages, id }) => ({
      body: {
        id,
        message: messages[messages.length - 1],
        deepSearch,
        externalSearch,
        foldersId,
      },
    });

    return new DefaultChatTransport({
      api: `${baseURL}/agent/ask-agent`,
      headers: () => ({
        'Content-Type': 'application/json',
        Authorization: Cookies.get('workstation_access_token') ?? '',
      }),
      fetch: async (url: RequestInfo | URL, init?: RequestInit) => {
        const response = await fetch(url, init);
        if (response.status === 402) {
          handlePaymentRequired();
        }
        return response;
      },
      prepareSendMessagesRequest: prepareRequest,
    });
  }, [deepSearch, externalSearch, foldersId, handlePaymentRequired]);

  const { messages, sendMessage, stop, status } = useChat<UIMessage>({
    id: chatId,
    transport,
    messages: initialMessages,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onError: (error) => {
      try {
        const errorData = JSON.parse(error.message);
        if (errorData?.errorCode === '402') {
          handlePaymentRequired();
        }
      } catch {
        // ignore parsing errors
      }
    },
  });

  const isLoading = status === 'submitted' || status === 'streaming';

  const handleNewChat = () => {
    setFoldersId([]);
    setChatId(generateId());
    setInput('');
    setInitialMessages([]);
    stop();
  };

  const handleLoadChat = (loadedMessages: UIMessage[], id: string) => {
    const typedMessages = loadedMessages as MessageWithFeedback[];
    const feedbacks: Record<string, PersistedFeedbackStruct> = {};
    for (const msg of typedMessages) {
      if (msg.feedback) {
        feedbacks[msg.id] = msg.feedback;
      }
    }

    setFeedbacksMap(feedbacks);
    setInitialMessages(typedMessages);
    setChatId(id);
  };

  useEffect(() => {
    const typedMessages = initialMessages as MessageWithFeedback[];
    const lastUserMessage = typedMessages.filter((msg) => msg.role === 'user').at(-1);
    const folders =
      lastUserMessage?.lastSearchFolders ??
      (lastUserMessage?.metadata as { lastSearchFolders?: string[] } | undefined)?.lastSearchFolders;
    if (folders && folders.length > 0) {
      setFoldersId(folders);
    } else {
      setFoldersId([]);
    }
  }, [initialMessages]);

  const toggleDeepSearch = () => {
    setDeepSearch((prev) => !prev);
  };

  const toggleExternalSearch = () => {
    setExternalSearch((prev) => !prev);
  };

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      const trimmed = input.trim();
      if (!trimmed) return;

      const message: UIMessage & { lastSearchFolders?: string[] } = {
        id: generateId(),
        role: 'user',
        parts: [{ type: 'text', text: trimmed }],
        lastSearchFolders: foldersId,
        metadata: {
          lastSearchFolders: foldersId,
        },
      };

      void sendMessage(message);
      setInput('');
    },
    [foldersId, input, sendMessage],
  );

  const feedbackHandlers = {
    getAllFeedbackMessages: () => feedbacksMap,
    getInMemoryFeedbackMessage: (messageId: string): PersistedFeedbackStruct | undefined =>
      feedbacksMap[messageId],
    addInMemoryFeedbackMessage: (messageId: string, feedback: PersistedFeedbackStruct) => {
      setFeedbacksMap((prev) => ({
        ...prev,
        [messageId]: feedback,
      }));
    },
    removeInMemoryFeedbackMessage: (messageId: string) => {
      setFeedbacksMap((prev) => {
        const newMap = { ...prev };
        delete newMap[messageId];
        return newMap;
      });
    },
  };

  const value: ChatContextType = {
    messages,
    handleSubmit,
    input,
    setInput,
    isLoading,
    stop,
    chatId,
    handleNewChat,
    handleLoadChat,
    deepSearch,
    toggleDeepSearch,
    externalSearch,
    toggleExternalSearch,
    feedbackHandlers,
    setFoldersId,
    foldersId,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
